import { For, Show, createEffect, createMemo, createResource, createSignal, onCleanup } from 'solid-js';
import Text from '../Text';
import Modal from '../Modal';
import ItemStackView from '../ItemStackView';
import LazyRender from '../LazyRender';
import { EmptyState } from '../EmptyState';
import { SearchInput } from '../SearchInput';
import { useTranslation } from '../../hooks/useTranslation';
import { useItemName } from '../../hooks/useItemName';
import { useCraftingResolver } from '../../services/craftingResolverContext';
import { fromIdentifier } from '../../services/itemRefAdapters';
import type { ItemStack } from '../../models/itemStack';
import type { ItemDef } from '../../services/craftingResolver';
import {
    BIOME_TINTS,
    DYEABLE_ITEMS,
    DYES,
    POTION_SPLASH_BIT,
    TINTED_TEXTURES,
    TINT_DEFAULTS,
    bakeIcon,
    blockTextureLayers,
    blockTextureSrc,
    dyeableLayers,
    itemLayers,
    loadBlockTextureNames,
    potionChoices,
    potionLayers,
    type BakedIcon,
    type DyeableItem,
    type IconRecipe,
    type PotionChoice,
} from '../../mapart/icons';
import styles from './IconDialog.module.css';

// Modal is the shell and ItemStackView draws every item tile. Block textures are no items, so they
// show as the plain texture, and the preview shows the baked result because no shared component can
// draw a dyed leather piece.
type Tab = 'items' | 'textures' | 'potions' | 'dyeable';

type Choice =
    | { tab: 'items'; item: ItemDef }
    | { tab: 'textures'; texture: string }
    | { tab: 'potions'; potion: PotionChoice }
    | { tab: 'dyeable'; dyeable: DyeableItem };

interface Swatch {
    colour: string;
    label: string;
}

const TABS: { tab: Tab; key: string; icon: string }[] = [
    { tab: 'items', key: 'MAPART_ICON_TAB_ITEMS', icon: 'fa-solid fa-gem' },
    { tab: 'textures', key: 'MAPART_ICON_TAB_TEXTURES', icon: 'fa-solid fa-cube' },
    { tab: 'potions', key: 'MAPART_ICON_TAB_POTIONS', icon: 'fa-solid fa-flask' },
    { tab: 'dyeable', key: 'MAPART_ICON_TAB_DYEABLE', icon: 'fa-solid fa-palette' },
];

/**
 * Tiles per lazily mounted block, so a full list does not decode every sprite on open. A multiple of
 * both column counts in the stylesheet, or every block would end on a ragged row.
 */
const TILES_PER_BLOCK = 48;
const BLOCK_HEIGHT_ESTIMATE = 320;

const DYE_ITEM_ID = 351;

/** The panel draws block renders one and a half times brighter, and the icon should match its tile. */
const BLOCK_RENDER_BRIGHTNESS = 1.5;
const LAST_BLOCK_ID = 255;

const readableTexture = (texture: string) => texture.replaceAll('_', ' ');

interface Props {
    open: boolean;
    onClose: () => void;
    /** `brightness` is the starting value for the new layer's brightness adjustment. */
    onInsert: (icon: Blob, name: string, brightness: number) => void;
}

export default function IconDialog(props: Props) {
    const { t } = useTranslation();
    const { stackName } = useItemName();
    const resolver = useCraftingResolver();
    const [tab, setTab] = createSignal<Tab>('items');
    const [query, setQuery] = createSignal('');
    const [choice, setChoice] = createSignal<Choice | null>(null);
    const [enchanted, setEnchanted] = createSignal(false);
    const [splash, setSplash] = createSignal(false);
    const [tint, setTint] = createSignal<string | null>(null);
    const [baked, setBaked] = createSignal<BakedIcon | null>(null);
    const [previewUrl, setPreviewUrl] = createSignal<string | null>(null);
    const [bakeFailed, setBakeFailed] = createSignal(false);

    const [textures] = createResource(
        () => props.open && tab() === 'textures',
        () => loadBlockTextureNames(),
    );

    const snbtFor = (effectId: number | null) => {
        const tags: string[] = [];
        if (effectId !== null) tags.push(`CustomPotionEffects:[{Id:${effectId}b,Amplifier:0b,Duration:0}]`);
        if (enchanted()) tags.push('ench:[{id:0s,lvl:1s}]');
        return tags.length > 0 ? `{${tags.join(',')}}` : undefined;
    };

    const itemStack = (item: ItemDef): ItemStack => ({
        ...fromIdentifier(`minecraft:${item.name}`, item.metadata, resolver()),
        snbt: snbtFor(null),
    });

    const potionStack = (potion: PotionChoice): ItemStack => ({
        ...fromIdentifier('minecraft:potion', splash() ? POTION_SPLASH_BIT : 0, resolver()),
        snbt: snbtFor(potion.effectId),
    });

    const dyeableStack = (item: string): ItemStack => ({
        ...fromIdentifier(`minecraft:${item}`, 0, resolver()),
        snbt: snbtFor(null),
    });

    const matchingItems = createMemo(() => {
        const itemCatalogue = resolver();
        if (!itemCatalogue) return [];
        const needle = query().trim().toLowerCase();
        const allItems = itemCatalogue.getAllItems();
        return needle ? allItems.filter((item) => itemCatalogue.matchesSearch(item.key, needle)) : allItems;
    });

    const matchingTextures = createMemo(() => {
        const needle = query().trim().toLowerCase().replaceAll(' ', '_');
        const names = textures() ?? [];
        return needle ? names.filter((name) => name.includes(needle)) : names;
    });

    const itemBlocks = createMemo(() => {
        const items = matchingItems();
        const blocks: ItemDef[][] = [];
        for (let start = 0; start < items.length; start += TILES_PER_BLOCK) {
            blocks.push(items.slice(start, start + TILES_PER_BLOCK));
        }
        return blocks;
    });

    const potions = potionChoices();

    const tintKind = () => {
        const current = choice();
        return current?.tab === 'textures' ? TINTED_TEXTURES[current.texture]?.tint : undefined;
    };

    const dyeName = (metadata: number) => {
        const dyeItem = resolver()?.getItemById(DYE_ITEM_ID, metadata);
        return dyeItem ? stackName({ itemId: dyeItem.id, damage: metadata, name: dyeItem.name }) : '';
    };

    const swatches = createMemo<Swatch[]>(() => {
        const current = choice();
        if (current?.tab === 'dyeable') {
            return [
                { colour: current.dyeable.defaultColour, label: t('MAPART_ICON_TINT_UNDYED') },
                ...DYES.map((dye) => ({
                    colour: current.dyeable.kind === 'fireworks_charge' ? dye.firework : dye.leather,
                    label: dyeName(dye.metadata),
                })),
            ];
        }
        const kind = tintKind();
        if (!kind) return [];
        const standard = { colour: TINT_DEFAULTS[kind], label: t('MAPART_ICON_TINT_DEFAULT') };
        if (kind !== 'grass' && kind !== 'foliage') return [standard];
        return [standard, ...BIOME_TINTS.map((biome) => ({ colour: biome[kind], label: t(biome.labelKey) }))];
    });

    const choose = (next: Choice) => {
        setChoice(next);
        if (next.tab === 'dyeable') {
            setTint(next.dyeable.defaultColour);
        } else if (next.tab === 'textures' && TINTED_TEXTURES[next.texture]) {
            setTint(TINT_DEFAULTS[TINTED_TEXTURES[next.texture].tint]);
        } else {
            setTint(null);
        }
    };

    const isChosen = (matches: (current: Choice) => boolean) => {
        const current = choice();
        return current !== null && matches(current);
    };

    const recipe = createMemo<IconRecipe | null>(() => {
        const current = choice();
        if (!current) return null;
        const layers =
            current.tab === 'items'
                ? itemLayers(current.item)
                : current.tab === 'textures'
                  ? blockTextureLayers(current.texture, tint())
                  : current.tab === 'potions'
                    ? potionLayers(current.potion.colour, splash())
                    : dyeableLayers(current.dyeable.kind, tint() ?? current.dyeable.defaultColour);
        return { layers, enchanted: enchanted() };
    });

    createEffect(() => {
        const current = recipe();
        setBaked(null);
        setBakeFailed(false);
        if (!current) return;
        let cancelled = false;
        onCleanup(() => {
            cancelled = true;
        });
        bakeIcon(current)
            .then((icon) => {
                if (!cancelled) setBaked(icon);
            })
            .catch(() => {
                if (!cancelled) setBakeFailed(true);
            });
    });

    createEffect(() => {
        const icon = baked();
        if (!icon) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(icon.blob);
        setPreviewUrl(url);
        onCleanup(() => URL.revokeObjectURL(url));
    });

    const choiceName = () => {
        const current = choice();
        if (!current) return '';
        if (current.tab === 'items') return stackName(itemStack(current.item));
        if (current.tab === 'textures') return readableTexture(current.texture);
        if (current.tab === 'potions') return stackName(potionStack(current.potion));
        return stackName(dyeableStack(current.dyeable.item));
    };

    const insert = () => {
        const icon = baked();
        const current = choice();
        if (!icon || !current) return;
        // Only the isometric block renders get the panel's brightening; a flat 16 pixel block item
        // such as a torch is the game's own texture and already as bright as it gets.
        const blockRender = current.tab === 'items' && current.item.id <= LAST_BLOCK_ID && !icon.upscaled;
        props.onInsert(icon.blob, choiceName(), blockRender ? BLOCK_RENDER_BRIGHTNESS : 1);
    };

    const switchTab = (next: Tab) => {
        setTab(next);
        setQuery('');
    };

    return (
        <Show when={props.open}>
            <Modal open={true} onClose={props.onClose} maxWidth="48rem" title={<Text val="MAPART_ICON_TITLE" />}>
                <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                    <div class={styles.tabs}>
                        <For each={TABS}>{(entry) => (
                            <button
                                type="button"
                                class="btn btn-sm"
                                classList={{ 'btn-primary': tab() === entry.tab, 'btn-secondary': tab() !== entry.tab }}
                                onClick={() => switchTab(entry.tab)}
                            >
                                <i class={entry.icon} /> {t(entry.key)}
                            </button>
                        )}</For>
                    </div>

                    <Show when={tab() === 'items' || tab() === 'textures'}>
                        <SearchInput
                            value={query()}
                            onInput={setQuery}
                            placeholder={t('MAPART_ICON_SEARCH')}
                            ariaLabel={t('MAPART_ICON_SEARCH')}
                        />
                    </Show>

                    <div class={styles.scroller}>
                        <Show when={tab() === 'items'}>
                            <Show when={resolver()} fallback={<div class={styles.waiting}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}>
                                <Show when={matchingItems().length > 0} fallback={
                                    <EmptyState illustration="generic" title={t('MAPART_ICON_NO_MATCH')} hint={t('MAPART_ICON_NO_MATCH_HINT')} />
                                }>
                                    <For each={itemBlocks()}>{(block) => (
                                        <LazyRender estimatedHeight={BLOCK_HEIGHT_ESTIMATE}>
                                            {() => (
                                                <div class={styles.grid}>
                                                    <For each={block}>{(item) => (
                                                        <button
                                                            type="button"
                                                            class={`btn btn-secondary ${styles.tile}`}
                                                            classList={{ [styles.tileActive]: isChosen((current) => current.tab === 'items' && current.item.key === item.key) }}
                                                            title={stackName(itemStack(item))}
                                                            onClick={() => choose({ tab: 'items', item })}
                                                        >
                                                            <ItemStackView stack={itemStack(item)} size="100%" bare showCount={false} showDurability={false} />
                                                        </button>
                                                    )}</For>
                                                </div>
                                            )}
                                        </LazyRender>
                                    )}</For>
                                </Show>
                            </Show>
                        </Show>

                        <Show when={tab() === 'textures'}>
                            <Show when={!textures.loading} fallback={<div class={styles.waiting}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>}>
                                <Show when={!textures.error} fallback={
                                    <EmptyState illustration="generic" title={t('MAPART_ICON_TEXTURES_FAILED')} hint={t('MAPART_ICON_TEXTURES_FAILED_HINT')} />
                                }>
                                    <Show when={matchingTextures().length > 0} fallback={
                                        <EmptyState illustration="generic" title={t('MAPART_ICON_NO_MATCH')} hint={t('MAPART_ICON_NO_MATCH_HINT')} />
                                    }>
                                        <div class={styles.grid}>
                                            <For each={matchingTextures()}>{(texture) => (
                                                <button
                                                    type="button"
                                                    class={`btn btn-secondary ${styles.tile}`}
                                                    classList={{ [styles.tileActive]: isChosen((current) => current.tab === 'textures' && current.texture === texture) }}
                                                    title={readableTexture(texture)}
                                                    onClick={() => choose({ tab: 'textures', texture })}
                                                >
                                                    <img class={styles.texture} src={blockTextureSrc(texture)} alt={readableTexture(texture)} loading="lazy" />
                                                    <Show when={TINTED_TEXTURES[texture]}>
                                                        <i class={`fa-solid fa-droplet ${styles.tintMark}`} />
                                                    </Show>
                                                </button>
                                            )}</For>
                                        </div>
                                    </Show>
                                </Show>
                            </Show>
                        </Show>

                        <Show when={tab() === 'potions'}>
                            <div class={styles.grid}>
                                <For each={potions}>{(potion) => (
                                    <button
                                        type="button"
                                        class={`btn btn-secondary ${styles.tile}`}
                                        classList={{ [styles.tileActive]: isChosen((current) => current.tab === 'potions' && current.potion.effectId === potion.effectId) }}
                                        title={stackName(potionStack(potion))}
                                        onClick={() => choose({ tab: 'potions', potion })}
                                    >
                                        <ItemStackView stack={potionStack(potion)} size="100%" bare showCount={false} />
                                    </button>
                                )}</For>
                            </div>
                        </Show>

                        <Show when={tab() === 'dyeable'}>
                            <div class={styles.grid}>
                                <For each={DYEABLE_ITEMS}>{(entry) => (
                                    <button
                                        type="button"
                                        class={`btn btn-secondary ${styles.tile}`}
                                        classList={{ [styles.tileActive]: isChosen((current) => current.tab === 'dyeable' && current.dyeable === entry) }}
                                        title={stackName(dyeableStack(entry.item))}
                                        onClick={() => choose({ tab: 'dyeable', dyeable: entry })}
                                    >
                                        <ItemStackView stack={dyeableStack(entry.item)} size="100%" bare showCount={false} />
                                    </button>
                                )}</For>
                            </div>
                            <p class={styles.hint}><Text val="MAPART_ICON_DYEABLE_HINT" /></p>
                        </Show>
                    </div>

                    <div class={styles.footer}>
                        <div class={styles.preview}>
                            <Show
                                when={previewUrl()}
                                fallback={
                                    <Show when={bakeFailed()} fallback={
                                        <Show when={choice()} fallback={<i class={`fa-solid fa-hand-pointer ${styles.previewIdle}`} />}>
                                            <i class="fa-solid fa-spinner fa-spin" />
                                        </Show>
                                    }>
                                        <i class={`fa-solid fa-triangle-exclamation ${styles.previewIdle}`} />
                                    </Show>
                                }
                            >
                                <img class={styles.previewImage} src={previewUrl()!} alt={choiceName()} />
                            </Show>
                        </div>

                        <div class={`v ${styles.options}`}>
                            <Show when={choice()} fallback={<span class={styles.hint}><Text val="MAPART_ICON_PICK_HINT" /></span>}>
                                <span class={styles.choiceName}>{choiceName()}</span>
                                <Show when={bakeFailed()}>
                                    <span class={styles.error}><Text val="MAPART_ICON_BAKE_FAILED" /></span>
                                </Show>
                                <Show when={baked()}>
                                    <span class={styles.hint}>{t('MAPART_ICON_SIZE', baked()!.width, baked()!.height)}</span>
                                </Show>
                            </Show>

                            <label class={styles.checkRow}>
                                <input type="checkbox" checked={enchanted()} onChange={(e) => setEnchanted(e.currentTarget.checked)} />
                                <span class={styles.fieldLabel}><Text val="MAPART_ICON_ENCHANTED" /></span>
                            </label>

                            <Show when={tab() === 'potions'}>
                                <label class={styles.checkRow}>
                                    <input type="checkbox" checked={splash()} onChange={(e) => setSplash(e.currentTarget.checked)} />
                                    <span class={styles.fieldLabel}><Text val="MAPART_ICON_SPLASH" /></span>
                                </label>
                            </Show>

                            <Show when={tint() !== null}>
                                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_ICON_TINT" /></span>
                                    <div class={styles.swatches}>
                                        <input type="color" value={tint() ?? '#ffffff'} onInput={(e) => setTint(e.currentTarget.value)} />
                                        <For each={swatches()}>{(swatch) => (
                                            <button
                                                type="button"
                                                class={`btn ${styles.swatch}`}
                                                classList={{ [styles.swatchActive]: tint() === swatch.colour }}
                                                style={{ background: swatch.colour }}
                                                title={swatch.label}
                                                aria-label={swatch.label}
                                                onClick={() => setTint(swatch.colour)}
                                            />
                                        )}</For>
                                    </div>
                                </div>
                            </Show>
                        </div>

                        <div class={styles.actions}>
                            <button type="button" class="btn btn-secondary" onClick={props.onClose}>
                                <Text val="CANCEL" />
                            </button>
                            <button type="button" class="btn btn-primary" disabled={!baked()} onClick={insert}>
                                <i class="fa-solid fa-plus" /> <Text val="MAPART_ICON_INSERT" />
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </Show>
    );
}
